import { DatePicker } from '@globalmed/atlas-ui';
import dayjs from 'dayjs';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styles from './ContractPeriodFields.module.css';
import {
  TCreateOrganizationForm,
  TOrganizationContractForm,
} from '../../schema';
import { useGlobalMedDisclaimer } from '../../utils/useGlobalMedDisclaimer';
import { IntegerInput } from '@/components/IntegerInput';
import {
  EOrganizationProvisioningScope,
  EOrganizationProvisioningType,
} from '@/types';
import { hasContractStarted } from '@/utils/organizationContracts';

type TProps = {
  index: number;
  disabled?: boolean;
  occupiedSeats?: number;
  savedContract?: TOrganizationContractForm;
};

/**
 * The fields of one provisioning contract: the counts its provisioning type
 * bills on (consults per month, or seats and station licenses for
 * organization-scoped seats) and its contract period. The end date is not
 * editable: it follows the start date, one year on, minus one day, while a
 * saved contract whose start is left as it was keeps its stored end.
 *
 * A saved contract is locked once it has started: none of its fields can
 * change.
 * @param props - Component props.
 * @param props.index - Index of the contract in `provisioning.contracts`.
 * @param props.disabled - When true, the fields are locked for editing (Org
 * Admins) and show a disclaimer tooltip on hover.
 * @param props.occupiedSeats - The organization's current occupied seats,
 * interpolated into the "cannot be lower than occupied" seats error message.
 * @param props.savedContract - The contract as it was loaded, when it is
 * saved already; absent for a contract added in this session.
 * @returns JSX.Element representing the contract's fields.
 */
export const ContractPeriodFields = ({
  index,
  disabled = false,
  occupiedSeats,
  savedContract,
}: TProps) => {
  const {
    control,
    watch,
    setValue,
    formState: { isSubmitting },
  } = useFormContext<TCreateOrganizationForm>();
  const { t } = useTranslation();
  const withDisclaimer = useGlobalMedDisclaimer(disabled);

  const type = watch('provisioning.type');
  const scope = watch('provisioning.scope');
  const isConsults = type === EOrganizationProvisioningType.CONSULTS_PER_MONTH;
  const isOrganizationSeats =
    type === EOrganizationProvisioningType.SEATS &&
    scope === EOrganizationProvisioningScope.ORGANIZATION;
  const currentYear = dayjs().year();

  const isLocked = hasContractStarted(savedContract?.contractStartDate);

  return (
    <div className={styles.flexColumn}>
      {isConsults && (
        <Controller
          name={`provisioning.contracts.${index}.consultsPerMonth`}
          control={control}
          render={({ field, fieldState }) =>
            withDisclaimer(
              <IntegerInput
                label={t(
                  'create-organization-modal.number-of-consults-per-month',
                )}
                placeholder={t(
                  'create-organization-modal.number-of-consults-per-month-placeholder',
                )}
                value={field.value ?? ''}
                onChange={field.onChange}
                min="1"
                width="100%"
                thousandSeparator
                disabled={isSubmitting || disabled || isLocked}
                isError={!!fieldState.error}
                message={t(fieldState.error?.message ?? '', {
                  field: t(
                    'create-organization-modal.number-of-consults-per-month',
                  ),
                })}
                required
              />,
            )
          }
        />
      )}

      {isOrganizationSeats && (
        <>
          <Controller
            name={`provisioning.contracts.${index}.seats`}
            control={control}
            render={({ field, fieldState }) =>
              withDisclaimer(
                <IntegerInput
                  label={t('create-organization-modal.number-of-seats')}
                  placeholder={t(
                    'create-organization-modal.number-of-seats-placeholder',
                  )}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  min="1"
                  width="100%"
                  thousandSeparator
                  disabled={isSubmitting || disabled || isLocked}
                  isError={!!fieldState.error}
                  message={t(fieldState.error?.message ?? '', {
                    field: t('create-organization-modal.number-of-seats'),
                    count: occupiedSeats,
                  })}
                  required
                />,
              )
            }
          />

          <Controller
            name={`provisioning.contracts.${index}.stationLicenses`}
            control={control}
            render={({ field, fieldState }) =>
              withDisclaimer(
                <IntegerInput
                  label={t('create-organization-modal.station-licenses')}
                  placeholder={t(
                    'create-organization-modal.station-licenses-placeholder',
                  )}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  min="1"
                  width="100%"
                  disabled={isSubmitting || disabled || isLocked}
                  isError={!!fieldState.error}
                  message={t(fieldState.error?.message ?? '', {
                    field: t('create-organization-modal.station-licenses'),
                  })}
                  required
                />,
              )
            }
          />
        </>
      )}

      <div className={styles.block}>
        <Controller
          name={`provisioning.contracts.${index}.contractStartDate`}
          control={control}
          render={({ field, fieldState }) =>
            withDisclaimer(
              <DatePicker
                label={t('create-organization-modal.contract-start-date')}
                disabled={disabled || isLocked}
                value={field.value ?? null}
                onChange={(date) => {
                  field.onChange(date);
                  const isSavedStart =
                    !!date &&
                    !!savedContract &&
                    dayjs(date).isSame(savedContract.contractStartDate, 'day');
                  setValue(
                    `provisioning.contracts.${index}.contractEndDate`,
                    isSavedStart
                      ? (savedContract?.contractEndDate ?? null)
                      : date
                        ? dayjs(date).add(1, 'year').subtract(1, 'day').toDate()
                        : null,
                    { shouldDirty: true, shouldValidate: true },
                  );
                }}
                isError={!!fieldState.error}
                message={t(fieldState.error?.message ?? '')}
                width={'100%'}
                sx={{ width: '265px' }}
                yearsRange={[currentYear - 5, currentYear + 10]}
                required
              />,
            )
          }
        />
        <Controller
          name={`provisioning.contracts.${index}.contractEndDate`}
          control={control}
          render={({ field }) =>
            withDisclaimer(
              <DatePicker
                label={t('create-organization-modal.contract-end-date')}
                disabled
                value={field.value ?? null}
                width={'100%'}
                sx={{ width: '265px' }}
                yearsRange={[currentYear - 5, currentYear + 11]}
                required
              />,
            )
          }
        />
      </div>
    </div>
  );
};
